// ADR-198: Reports → Scheduled Jobs — formatting helpers (pure, unit-tested).
import { JOB_CATEGORY_LABELS } from '../constants/scheduledJobs';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const pad = (n) => String(n).padStart(2, '0');

/** node-schedule fields arrive as a number, an array, or a serialized Range { start, end, step }. */
export const toList = (value) => {
  if (value === undefined || value === null) return null;
  if (Array.isArray(value)) return value.flatMap((v) => toList(v) || []);
  if (typeof value === 'object' && Number.isFinite(value.start) && Number.isFinite(value.end)) {
    const step = Number.isFinite(value.step) && value.step > 0 ? value.step : 1;
    const out = [];
    for (let v = value.start; v <= value.end; v += step) out.push(v);
    return out;
  }
  return Number.isFinite(Number(value)) ? [Number(value)] : null;
};

const isContiguous = (sorted) => sorted.every((v, i) => i === 0 || v === sorted[i - 1] + 1);

export const describeDays = (dayOfWeek) => {
  const days = toList(dayOfWeek);
  if (!days || days.length === 0) return 'Every day';
  const sorted = [...new Set(days.map((d) => ((d % 7) + 7) % 7))].sort((a, b) => a - b);
  if (sorted.length === 7) return 'Every day';
  if (sorted.length === 2 && sorted[0] === 0 && sorted[1] === 6) return 'Weekends';
  if (sorted.length >= 3 && isContiguous(sorted)) return `${DAY_NAMES[sorted[0]]}–${DAY_NAMES[sorted[sorted.length - 1]]}`;
  return sorted.map((d) => DAY_NAMES[d]).join(', ');
};

const describeTimes = (hour, minute) => {
  const hours = toList(hour);
  const minutes = toList(minute) || [0];
  if (!hours) {
    if (minutes.length === 1) return `hourly at :${pad(minutes[0])}`;
    const sorted = [...minutes].sort((a, b) => a - b);
    const step = sorted[1] - sorted[0];
    const even = sorted[0] === 0 && sorted.every((m, i) => m === i * step) && 60 % step === 0 && sorted.length === 60 / step;
    return even ? `every ${step} min` : `hourly at ${sorted.map((m) => `:${pad(m)}`).join(', ')}`;
  }
  const sortedHours = [...hours].sort((a, b) => a - b);
  if (minutes.length === 1 && sortedHours.length >= 3 && isContiguous(sortedHours)) {
    const m = pad(minutes[0]);
    return `hourly ${pad(sortedHours[0])}:${m}–${pad(sortedHours[sortedHours.length - 1])}:${m}`;
  }
  const times = sortedHours.flatMap((h) => [...minutes].sort((a, b) => a - b).map((m) => `${pad(h)}:${pad(m)}`));
  return `at ${times.join(', ')}`;
};

/**
 * "Mon–Fri at 07:00", "Every day, every 15 min", "Mon–Fri hourly 10:00–19:00", "Manual only".
 * Times are in the scheduler's timezone; callers show that timezone next to it.
 */
export const describeSchedule = (schedule) => {
  if (!schedule) return '—';
  switch (schedule.type) {
    case 'manual-only':
      return 'Manual only';
    case 'cron':
      return `Cron ${schedule.expression}`;
    case 'recurrence': {
      const days = describeDays(schedule.dayOfWeek);
      const times = describeTimes(schedule.hour, schedule.minute);
      return times.startsWith('every') || times.startsWith('hourly at')
        ? `${days}, ${times}`
        : `${days} ${times}`;
    }
    default:
      return 'Custom schedule';
  }
};

/** "in 2h 5m", "3m ago", "in 2d 4h", "now". */
export const formatRelative = (target, now = new Date()) => {
  if (!target) return '';
  const t = new Date(target).getTime();
  if (Number.isNaN(t)) return '';
  const diff = t - new Date(now).getTime();
  const abs = Math.abs(diff);
  if (abs < 60 * 1000) return 'now';
  const totalMinutes = Math.floor(abs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  let text;
  if (days > 0) text = hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  else if (hours > 0) text = minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  else text = `${minutes}m`;
  return diff > 0 ? `in ${text}` : `${text} ago`;
};

/** Groups rows by category in the catalog's order; unknown categories go last. */
export const groupByCategory = (schedulers = [], categories = []) => {
  const order = [...categories];
  schedulers.forEach((s) => {
    if (!order.includes(s.category)) order.push(s.category);
  });
  return order
    .map((category) => ({
      category,
      label: JOB_CATEGORY_LABELS[category] || category,
      jobs: schedulers.filter((s) => s.category === category),
    }))
    .filter((group) => group.jobs.length > 0);
};

export const summarizeJobs = (schedulers = []) => ({
  total: schedulers.length,
  running: schedulers.filter((s) => s.isRunning).length,
  failing: schedulers.filter((s) => s.lastRun?.outcome === 'FAILED').length,
  skipped: schedulers.filter((s) => (s.skippedCount || 0) > 0).length,
  neverRan: schedulers.filter((s) => !s.lastRun?.startedAt && s.state !== 'MANUAL_ONLY').length,
});

/** The soonest scheduled fire across all jobs. */
export const nextFire = (schedulers = []) => schedulers
  .filter((s) => s.nextRun)
  .reduce((best, s) => (!best || new Date(s.nextRun) < new Date(best.nextRun) ? s : best), null);
